import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import MediaCard from "@/components/base/MediaCard";
import FilterPanel, { type FilterState } from "@/pages/category/components/FilterPanel";
import { mediaList } from "@/mocks/media";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import NotFound from "@/pages/NotFound";

const ALL = "全部";
const PAGE_SIZE = 12;

const sortOptions = [
  { value: "updated", label: "更新时间" },
  { value: "views", label: "热度" },
  { value: "rating", label: "评分" },
];

export default function Category() {
  const { type } = useParams<{ type: string }>();
  const isMovie = type === "movie";
  const isTv = type === "tv";

  const [searchParams, setSearchParams] = useSearchParams();

  const initialFilters: FilterState = {
    genre: searchParams.get("genre") || ALL,
    region: searchParams.get("region") || ALL,
    year: searchParams.get("year") || ALL,
    sort: searchParams.get("sort") || "updated",
  };
  const [filters, setFilters] = useState<FilterState>(initialFilters);
  const [visible, setVisible] = useState(PAGE_SIZE);

  const pageTitle = isMovie ? "电影分类" : "电视剧分类";
  useDocumentTitle(
    `${pageTitle} - 光棍影院在线影视平台`,
    isMovie
      ? "光棍影院电影分类页，收录动作、科幻、爱情、悬疑等多种类型电影，支持按类型、地区、年份筛选与热度评分排序。"
      : "光棍影院电视剧分类页，汇集古装、都市、悬疑、家庭等热门剧集，支持按类型、地区、年份筛选与热度评分排序。",
    );

  const typeItems = useMemo(
    () => mediaList.filter((item) => item.type === (isMovie ? "movie" : "tv")),
    [isMovie]
  );

  const genres = useMemo(
    () => Array.from(new Set(typeItems.flatMap((item) => item.genre))),
    [typeItems]
  );
  const regions = useMemo(
    () => Array.from(new Set(typeItems.map((item) => item.region))),
    [typeItems]
  );
  const years = useMemo(() => {
    const nums = Array.from(new Set(typeItems.map((item) => item.year))).sort(
      (a, b) => b - a
    );
    return [...nums.map(String), "更早"];
  }, [typeItems]);

  const filtered = useMemo(() => {
    let list = typeItems.filter((item) => {
      if (filters.genre !== ALL && !item.genre.includes(filters.genre)) return false;
      if (filters.region !== ALL && item.region !== filters.region) return false;
      if (filters.year === "更早") {
        if (item.year >= 2024) return false;
      } else if (filters.year !== ALL && String(item.year) !== filters.year) {
        return false;
      }
      return true;
    });

    list = [...list].sort((a, b) => {
      if (filters.sort === "views") return b.views - a.views;
      if (filters.sort === "rating") return b.rating - a.rating;
      return b.updatedAt.localeCompare(a.updatedAt);
    });
    return list;
  }, [typeItems, filters]);

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [filters]);

  const updateFilters = (patch: Partial<FilterState>) => {
    const next = { ...filters, ...patch };
    setFilters(next);
    const params = new URLSearchParams();
    if (next.genre !== ALL) params.set("genre", next.genre);
    if (next.region !== ALL) params.set("region", next.region);
    if (next.year !== ALL) params.set("year", next.year);
    if (next.sort !== "updated") params.set("sort", next.sort);
    setSearchParams(params, { replace: true });
  };

  const resetFilters = () => {
    setFilters({ genre: ALL, region: ALL, year: ALL, sort: "updated" });
    setSearchParams({}, { replace: true });
  };

  const [drawerOpen, setDrawerOpen] = useState(false);
  const shown = filtered.slice(0, visible);

  if (!isMovie && !isTv) {
    return <NotFound />;
  }

  const activeCount = [
    filters.genre !== ALL,
    filters.region !== ALL,
    filters.year !== ALL,
    filters.sort !== "updated",
  ].filter(Boolean).length;

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Navbar />
      <main className="flex-1 w-full px-4 md:px-8 pt-24 md:pt-28 pb-14">
        <nav className="text-sm text-foreground-500 mb-4 flex items-center gap-1.5">
          <Link to="/" className="hover:text-primary-600 transition-colors">
            首页
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-800 font-medium">{pageTitle}</span>
        </nav>

        <div className="mb-6 md:mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">
            {isMovie ? "电影频道" : "电视剧频道"}
          </h1>
          <p className="mt-2 text-sm md:text-base text-foreground-500 max-w-2xl">
            {isMovie
              ? "精选各类院线电影与口碑佳作，涵盖动作、科幻、爱情、悬疑等丰富题材，快来挑选你的今晚片单。"
              : "汇集热播剧集与经典剧作，古装都市、悬疑家庭一应俱全，支持按类型、地区、年份快速筛选。"}
          </p>
        </div>

        <div className="flex items-center justify-between mb-4 lg:hidden">
          <p className="text-sm text-foreground-600">
            共 <span className="font-semibold text-accent-600">{filtered.length}</span> 部作品
          </p>
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap"
          >
            <i className="ri-filter-3-line"></i>
            筛选{activeCount > 0 ? `（${activeCount}）` : ""}
          </button>
        </div>

        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          <aside className="hidden lg:block w-64 shrink-0">
            <div className="sticky top-24 rounded-lg border border-background-200 bg-background-100 p-5">
              <FilterPanel
                filters={filters}
                genres={genres}
                regions={regions}
                years={years}
                sorts={sortOptions}
                onChange={updateFilters}
                onReset={resetFilters}
              />
            </div>
          </aside>

          <div className="flex-1 min-w-0">
            <div className="hidden lg:flex items-center justify-between mb-4">
              <p className="text-sm text-foreground-600">
                共 <span className="font-semibold text-accent-600">{filtered.length}</span> 部作品
              </p>
              <div className="flex items-center gap-2">
                {sortOptions.map((s) => (
                  <button
                    key={s.value}
                    type="button"
                    onClick={() => updateFilters({ sort: s.value })}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition-colors ${
                      filters.sort === s.value
                        ? "bg-accent-500 text-foreground-950"
                        : "bg-background-100 text-foreground-600 hover:bg-secondary-100"
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            {shown.length > 0 ? (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
                  {shown.map((item) => (
                    <MediaCard key={item.id} item={item} />
                  ))}
                </div>

                {visible < filtered.length && (
                  <div className="flex justify-center mt-8">
                    <button
                      type="button"
                      onClick={() => setVisible((v) => v + PAGE_SIZE)}
                      className="inline-flex items-center gap-2 px-6 py-2.5 rounded-md border border-background-300 text-sm font-medium text-foreground-800 hover:border-primary-400 hover:text-primary-600 transition-colors whitespace-nowrap"
                    >
                      <i className="ri-loader-4-line"></i>
                      加载更多（{filtered.length - visible}）
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="rounded-lg border border-background-200 bg-background-100 py-20 flex flex-col items-center text-center">
                <span className="w-16 h-16 rounded-full bg-background-200 flex items-center justify-center mb-4">
                  <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
                </span>
                <p className="text-base font-semibold text-foreground-800">
                  暂无符合条件的影视内容
                </p>
                <p className="text-sm text-foreground-500 mt-1">
                  试试调整筛选条件，或重置后重新浏览
                </p>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap"
                >
                  <i className="ri-refresh-line"></i>
                  重置筛选
                </button>
              </div>
            )}
          </div>
        </div>
      </main>

      {drawerOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div
            className="absolute inset-0 bg-foreground-950/50"
            onClick={() => setDrawerOpen(false)}
          />
          <div className="absolute inset-x-0 bottom-0 max-h-[85vh] overflow-y-auto rounded-t-2xl bg-background-50 p-5 animate-fade-up">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-bold text-foreground-950 font-heading">筛选</h2>
              <button
                type="button"
                aria-label="关闭筛选"
                onClick={() => setDrawerOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-700"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
            <FilterPanel
              filters={filters}
              genres={genres}
              regions={regions}
              years={years}
              sorts={sortOptions}
              onChange={updateFilters}
              onReset={resetFilters}
            />
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="w-full mt-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap"
            >
              查看 {filtered.length} 部作品
            </button>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}